import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Menu, Moon, Search, Sun } from 'lucide-react';
import { Avatar, Button, Dropdown, Input } from '@/components/ui';
import { UserMenu } from './UserMenu';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { mockApi } from '@/services/mockApi';
import { cn } from '@/utils/cn';

export function Header({ onOpenMobileSidebar, breadcrumb }) {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const displayName = user?.name || user?.email || 'Usuário';
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const searchRef = useRef(null);

  useEffect(() => {
    mockApi.getNotifications().then(setNotifications);
  }, []);

  useEffect(() => {
    const onClick = (event) => {
      if (!searchRef.current?.contains(event.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      const data = await mockApi.search(query);
      setResults(data);
      setSearchOpen(true);
    }, 200);

    return () => clearTimeout(timer);
  }, [query]);

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.read).length,
    [notifications],
  );

  return (
    <header className="sticky top-0 z-30 flex h-[var(--header-height)] items-center justify-between gap-3 border-b border-[var(--border)] bg-[var(--card)]/90 px-4 backdrop-blur">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onOpenMobileSidebar}
          aria-label="Abrir menu"
        >
          <Menu size={18} />
        </Button>
        <div className="hidden min-w-0 md:block">{breadcrumb}</div>
      </div>

      <div className="relative mx-2 hidden w-full max-w-md lg:block" ref={searchRef}>
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]"
          aria-hidden
        />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => query && setSearchOpen(true)}
          placeholder="Buscar clientes, contas, documentos..."
          className="pl-9"
          aria-label="Busca global"
        />
        {searchOpen && query.trim() ? (
          <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-96 overflow-y-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] p-2 shadow-[var(--shadow)]">
            {!results.length ? (
              <p className="px-3 py-4 text-sm text-[var(--muted-foreground)]">
                Nenhum resultado para “{query}”.
              </p>
            ) : (
              results.map((group) => (
                <div key={group.type} className="mb-2 last:mb-0">
                  <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
                    {group.type}
                  </p>
                  {group.items.map((item) => (
                    <button
                      key={`${group.type}-${item.id}`}
                      type="button"
                      className="flex w-full rounded-md px-3 py-2 text-left text-sm hover:bg-[var(--muted)]"
                      onClick={() => {
                        setSearchOpen(false);
                        setQuery('');
                        navigate(item.href);
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          aria-label="Buscar"
          onClick={() => {
            const term = window.prompt('Buscar clientes, contas, documentos...');
            if (term) {
              mockApi.search(term).then((data) => {
                const first = data[0]?.items?.[0];
                if (first) navigate(first.href);
              });
            }
          }}
        >
          <Search size={18} />
        </Button>

        <Dropdown
          trigger={
            <Button variant="ghost" size="icon" aria-label="Notificações" className="relative">
              <Bell size={18} />
              {unreadCount > 0 ? (
                <span className="absolute right-1.5 top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--destructive)] px-1 text-[10px] font-semibold text-white">
                  {unreadCount}
                </span>
              ) : null}
            </Button>
          }
          className="w-80"
        >
          {({ close }) => (
            <div className="p-1">
              <p className="px-3 py-2 text-sm font-semibold">Notificações</p>
              {notifications.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={cn(
                    'flex w-full flex-col gap-0.5 rounded-md px-3 py-2 text-left hover:bg-[var(--muted)]',
                    !item.read && 'bg-[var(--primary)]/5',
                  )}
                  onClick={() => {
                    close();
                    navigate(item.href);
                  }}
                >
                  <span className="text-sm font-medium">{item.title}</span>
                  <span className="text-xs text-[var(--muted-foreground)]">{item.description}</span>
                  <span className="text-[11px] text-[var(--muted-foreground)]">{item.time}</span>
                </button>
              ))}
            </div>
          )}
        </Dropdown>

        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </Button>

        <div className="hidden items-center gap-2 sm:flex">
          <Avatar name={displayName} size="sm" />
          <UserMenu />
        </div>
        <div className="sm:hidden">
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
